import { useCallback, useMemo } from "react";

import { addOntypes } from "Giving/constants";
import calculateAddOnGiftAmount from "Payments/PaymentPage/PaymentFormScreen/AddOnGiftsSection/calculateAddOnGiftAmount";
import cloneDeep from "lodash/cloneDeep";
import round from "lodash/round";

import { getAmountInCents } from "Utilities/Amount/getAmountInCents";

import { useRecurringGifts } from "../../RecurringGiftsContext";
import recurringGiftsActions from "../../actions";

const useDeletedAddOnGifts = () => {
  const {
    state: { editModal: { addOnGifts, deletedAddOnGifts } = {} },
    dispatch,
  } = useRecurringGifts();
  const [, { setAddOnGifts }] = useAddOnGifts();

  const addDeletedAddOnGift = useCallback(
    (addOnGiftID) => {
      const newAddOnGifts = cloneDeep(addOnGifts);
      delete newAddOnGifts[addOnGiftID];
      setAddOnGifts(newAddOnGifts);
      dispatch(recurringGiftsActions.addDeletedAddOnGift(addOnGiftID));
    },
    [addOnGifts, dispatch, setAddOnGifts]
  );

  return [deletedAddOnGifts, addDeletedAddOnGift];
};

const useAddOnGifts = () => {
  const {
    state: { editModal: { addOnGifts } = {} },
    dispatch,
  } = useRecurringGifts();

  const setAddOnGifts = useCallback(
    (newAddOnGifts = []) => {
      dispatch(recurringGiftsActions.setAddOnGifts(newAddOnGifts));
    },
    [dispatch]
  );

  return [addOnGifts, { setAddOnGifts }];
};

const useHasAddOnGifts = () => {
  const [addOnGifts] = useAddOnGifts();

  return useMemo(() => {
    return Object.entries(addOnGifts).length > 0;
  }, [addOnGifts]);
};

const useAddonGiftsAsPayload = () => {
  const [addOnGifts] = useAddOnGifts();

  return useMemo(
    () =>
      Object.values(addOnGifts).map(({ amount, addOnGiftID }) => ({
        amount: getAmountInCents(amount),
        addOnGiftID,
      })),
    [addOnGifts]
  );
};

const useCtfData = () => {
  const {
    state: { ctfData },
  } = useRecurringGifts();
  return ctfData;
};

const usePaymentMethodType = () => {
  const {
    state: {
      editModal: {
        giftWithEdits: { paymentMethod: { paymentType } = {} } = {},
      },
    },
  } = useRecurringGifts();
  return paymentType;
};

const typeConverter = {
  [addOntypes.AMOUNT]: "flat",
  [addOntypes.COVER_THE_FEES]: "ctf",
  [addOntypes.PERCENTAGE]: "percentage",
};

const useUpdateAddOnAmounts = () => {
  const [addOnGifts, { setAddOnGifts }] = useAddOnGifts();
  const ctfData = useCtfData();
  const oldPaymentType = usePaymentMethodType();
  const oldAmount = useGetMainGiftAmount();

  const updateAddOnAmounts = useCallback(
    ({
      newAmount,
      newPaymentType,
      addOnGiftID: addOnIdJustUpdated,
      newGiftAmount,
    }) => {
      const addOnGiftsArray = Object.values(cloneDeep(addOnGifts)).sort(
        ({ type }) => {
          // CTF needs to be calculated last
          if (type === addOntypes.COVER_THE_FEES) return 1;
          return -1;
        }
      );

      addOnGiftsArray.forEach(
        ({ percentage, type, amount, addOnGiftID }, idx) => {
          // this is inside the function because we need to use the already updated value if availabe (because CTF is cumulative)
          const addOnGiftAmounts = addOnGiftsArray.map(
            ({ amount: localAmount, addOnGiftID: addOnID }) => ({
              amount: Number(localAmount),
              addOnID,
              isChecked: true,
            })
          );

          if (addOnGiftID === addOnIdJustUpdated) {
            addOnGiftsArray[idx].amount = newGiftAmount;
            return;
          }
          // Updating the source object so that CTF can refer to the new amount when calculating.
          addOnGiftsArray[idx].amount = round(
            calculateAddOnGiftAmount(
              newAmount || oldAmount,
              addOnGiftAmounts,
              {
                type: typeConverter[type],
                presetAmount: amount,
                percentage,
                addOnID: addOnGiftID,
              },
              ctfData,
              newPaymentType || oldPaymentType
            ),
            2
          );
        }
      );

      setAddOnGifts(
        Object.fromEntries(
          addOnGiftsArray.map(({ addOnGiftID, ...gift }) => [
            addOnGiftID,
            { addOnGiftID, ...gift },
          ])
        )
      );
    },
    [addOnGifts, ctfData, oldAmount, oldPaymentType, setAddOnGifts]
  );

  return updateAddOnAmounts;
};

// This is seperate to be resused by useUpdateAddOnAmounts without causing stack overflow.
const useGetMainGiftAmount = () => {
  const {
    state: { editModal: { mainGift: { amount } = {} } = {} },
  } = useRecurringGifts();
  return amount;
};

const useMainGiftAmount = () => {
  const amount = useGetMainGiftAmount();
  const { dispatch } = useRecurringGifts();
  const updateAddOnAmounts = useUpdateAddOnAmounts();
  const paymentMethodType = usePaymentMethodType();

  const setMainGiftAmount = useCallback(
    (newAmount) => {
      if (Number(newAmount) > 100000) return;
      dispatch(recurringGiftsActions.setMainGiftAmount(newAmount));
      updateAddOnAmounts({
        newAmount,
        newPaymentType: paymentMethodType,
      });
    },
    [dispatch, paymentMethodType, updateAddOnAmounts]
  );

  return [amount, setMainGiftAmount];
};

// proxy to useMainGiftAmount for...historical reasons.
const useAmount = () => {
  const [mainGiftAmount, setMainGiftAmount] = useMainGiftAmount();
  return [mainGiftAmount, setMainGiftAmount];
};

const useSubtotal = () => {
  const [mainGiftAmount] = useMainGiftAmount();
  const [addOnGifts] = useAddOnGifts();

  return useMemo(() => {
    const addOnGiftsArray = Object.values(addOnGifts);
    const addOnGiftsArrayTotal = addOnGiftsArray.reduce(
      (currentTotal, { amount = 0 }) => currentTotal + Number(amount || 0),
      0
    );
    const mainGiftAmountNumber = Number(mainGiftAmount) || 0;
    const total = addOnGiftsArrayTotal + mainGiftAmountNumber;
    if (total % 1 !== 0) {
      //check if total is not a whole number
      return Number(total).toFixed(2);
    }
    return Number(total).toString();
  }, [addOnGifts, mainGiftAmount]);
};

const useSaveButtonText = () => {
  const hasAddOnGifts = useHasAddOnGifts();
  const subTotal = useSubtotal();

  const saveButtonText = useMemo(() => {
    if (!hasAddOnGifts) return "Save";
    return `Save my $${subTotal} gift`;
  }, [hasAddOnGifts, subTotal]);

  return saveButtonText;
};

export {
  useAddOnGifts,
  useHasAddOnGifts,
  useAddonGiftsAsPayload,
  useAmount,
  useUpdateAddOnAmounts,
  useMainGiftAmount,
  useSubtotal,
  useDeletedAddOnGifts,
  useSaveButtonText,
};
